/**
 * Sidebar feature gating — the rule, isolated from the rail that renders it.
 *
 * Lives in its own leaf module (no React, no Firebase, no network) so it can be
 * unit-tested directly. Importing Sidebar.tsx in jsdom pulls the Firebase auth
 * chain in through adminApi and blows up before any assertion runs.
 */
import { areasOf, isAvailableFor, PracticeArea, Role } from '../../types/practiceArea';

/**
 * Show a nav item if it has no feature key, or the workspace makes it available.
 *
 * Gating derives from the ACTIVE ROLE (sessionStorage `nyayabodh_active_role`),
 * not the lens alone. Two reasons, both of which shipped an empty "Case Work"
 * section to civil lawyers:
 *
 *  1. Role and lens live in different stores and can diverge — the role is
 *     per-tab sessionStorage, the lens is shared localStorage, so switching
 *     workspace in another tab leaves this tab with role=civil, lens=criminal.
 *     The switcher label reads from the role, so the rail could say
 *     "Consumer & Civil" while gating on criminal.
 *  2. The `civil` role spans six practice areas, but the civil tools are mapped
 *     to `civil_consumer` only. A civil lawyer whose primary area is
 *     civil_property/family/tax/labor/other therefore matched nothing.
 *
 * So: a feature is visible when it is available for ANY practice area in the
 * active role. That matches the backend, which authorises on the role
 * (`require_role("civil")`) — the sidebar must not hide a page the API serves.
 * Falls back to the lens when no role is set (pre-roles sessions / "All areas",
 * where isAvailableFor already shows the union).
 */
export const navItemVisible = (
  item: { feature?: string; flag?: string },
  lens: PracticeArea | null,
  role: Role | null,
  flags?: Record<string, boolean>,
): boolean => {
  // A server flag (kill switch) must be positively ON. Missing / loading /
  // errored all read as OFF, so the item is absent rather than a dead link.
  if (item.flag && flags?.[item.flag] !== true) return false;
  if (!item.feature) return true;
  if (!role) return isAvailableFor(item.feature, lens);
  const feature = item.feature;
  return [...areasOf(role)].some((area) => isAvailableFor(feature, area));
};

/** The workspace boundary this tab is in — same key the axios interceptor sends. */
export const readActiveRole = (): Role | null => {
  try {
    const raw = sessionStorage.getItem('nyayabodh_active_role');
    return raw === 'criminal' || raw === 'civil' || raw === 'corporate' ? raw : null;
  } catch {
    return null; // storage blocked — fall back to the lens
  }
};
